import React, { useState, useEffect } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import NavigationTour from './NavigationTour';
import { useAuth } from '../../contexts/AuthContext';
import tourService from '../../services/tourService';
import api from '../../services/api';

// Flip to true to re-enable the auto-popup tour for new users.
// The manual help button (bottom-right) is unaffected by this flag.
const AUTO_START_TOUR_ENABLED = false;

export const OnboardingTrigger = () => {
  const [showNavigationTour, setShowNavigationTour] = useState(false);
  const [userStatus, setUserStatus] = useState({
    hasResume: false,
    hasProfile: false,
    hasApplications: false
  });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check user status on mount
  useEffect(() => {
    if (isAuthenticated) {
      checkUserStatus().then(() => {
        if (!AUTO_START_TOUR_ENABLED) {
          return;
        }
        // Auto-start for new users only once
        if (!tourService.isTourCompleted('main_onboarding')) {
          tryAutoStartTour();
        }
      });
    }
  }, [isAuthenticated]);

  const checkUserStatus = async () => {
    try {
      // Check for resumes
      const resumeResponse = await api.get('/api/resumes');
      const hasResume = Array.isArray(resumeResponse.data) && resumeResponse.data.length > 0;

      // Check for profile
      const profileResponse = await api.get('/api/professional-profile');
      const hasProfile = Array.isArray(profileResponse.data) && profileResponse.data.length > 0;

      // Check for applications (placeholder - implement based on your API)
      const hasApplications = false; // You can implement this check

      setUserStatus({ hasResume, hasProfile, hasApplications });
    } catch (error) {
      console.error('Error checking user status:', error);
    }
  };

  const waitForElements = (ids, attempts = 10, intervalMs = 300) => {
    return new Promise((resolve) => {
      let tries = 0;
      const check = () => {
        const ready = ids.every(id => document.getElementById(id));
        if (ready) {
          resolve(true);
          return;
        }
        tries += 1;
        if (tries >= attempts) {
          resolve(false);
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });
  };

  const requiredElements = [
    'dashboard-nav',
    'professional-profile-nav',
    'resumes-nav', 
    'jobs-nav',
    'applications-nav',
    'mock-interview-nav'
  ];

  const tryAutoStartTour = async () => {
    const ready = await waitForElements(requiredElements, 10, 300);
    if (ready) {
      tourService.startTour('main_onboarding', navigate, userStatus);
      setShowNavigationTour(false);
    } else {
      setShowNavigationTour(true);
    }
  };

  const handleStartTour = async () => {
    // Manual restart should force even if completed
    const ready = await waitForElements(requiredElements, 10, 200);
    if (ready) {
      tourService.startTour('main_onboarding', { force: true }, navigate, userStatus);
      setShowNavigationTour(false);
    } else {
      // Fallback to old system if elements aren't ready
      setShowNavigationTour(true);
    }
  };

  const handleTourComplete = () => {
    setShowNavigationTour(false);
    tourService.markTourCompleted('main_onboarding');
  };

  return (
    <>
      {isAuthenticated && (
        <button
          onClick={handleStartTour}
          className="fixed bottom-6 right-6 bg-primary-600 text-white rounded-full p-3 shadow-lg hover:bg-primary-700 transition-all duration-200 transform hover:scale-105 z-30"
          aria-label="Start navigation tour"
          title="Get help with navigation"
        >
          <FiHelpCircle className="h-6 w-6" />
        </button>
      )}
      
      <NavigationTour 
        isActive={showNavigationTour}
        onComplete={handleTourComplete}
        userStatus={userStatus}
      />
    </>
  );
};

export default OnboardingTrigger;